import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { stateEnums, mailingStateEnums } from '~utils/enums';
import AutocompleteInput from '~components/common/AddressAutocomplete/Input';
import { useFormikContext } from 'formik';
import InputGroup from '~components/forms/inputs/Field';
import SelectGroup from '~components/forms/inputs/Select';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';

export const AddressBlockSchema = {
  address1: Yup.string().required('Street address is required'),
  address2: Yup.string().nullable(),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipcode: Yup.string()
    .required('ZIP code is required')
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(5, 'Must be exactly 5 digits')
    .max(5, 'Must be exactly 5 digits'),
};

export const AddressBlockSchemaStateOnly = {
  state: Yup.string().required('State is required'),
};

export function Address1Field({
  label,
  helpText,
  errors,
  prepend,
  elementId,
  required,
  onSelect,
  hasManualOption,
}) {
  const sourcing = useSelector((state) => state.sourcing);

  const formikProps = useFormikContext();

  const address1Name = `${prepend}address1`;
  const cityName = `${prepend}city`;
  const stateName = `${prepend}state`;
  const zipcodeName = `${prepend}zipcode`;

  const handleInputChange = (newVal) => {
    formikProps.setFieldValue(address1Name, newVal);
  };

  const handleAutocompleteSelect = (place) => {
    onSelect();

    if (place?.streetLine) {
      formikProps.setFieldValue(address1Name, place.streetLine || '');
      formikProps.setFieldValue(cityName, place.city || '');
      formikProps.setFieldValue(stateName, place.state || '');
      formikProps.setFieldValue(zipcodeName, place.zipcode || '');
    }
  };

  const handleBlur = () => {
    formikProps.setFieldTouched(address1Name, true);
  };

  return (
    <InputGroup
      name={address1Name}
      label={label}
      errors={errors}
      required={required}
      helpText={
        sourcing.edition === 'college'
          ? 'We recommend registering to vote using your campus address.'
          : helpText
      }
    >
      <AutocompleteInput
        name={address1Name}
        onInputChange={handleInputChange}
        onAutocompleteSelect={handleAutocompleteSelect}
        onBlur={handleBlur}
        required={required}
        elementId={elementId}
        errors={errors}
        hasManualOption={hasManualOption}
      />
    </InputGroup>
  );
}

Address1Field.propTypes = {
  label: PropTypes.string,
  helpText: PropTypes.string,
  errors: PropTypes.object.isRequired,
  prepend: PropTypes.string,
  elementId: PropTypes.string,
  required: PropTypes.bool,
  onSelect: PropTypes.func,
  hasManualOption: PropTypes.bool,
};

Address1Field.defaultProps = {
  label: 'Street address',
  helpText: '',
  prepend: '',
  hasManualOption: false,
};

export function Address2Field({ prepend, label, errors, elementId }) {
  return (
    <InputGroup
      name={`${prepend}address2`}
      label={label}
      errors={errors}
      elementId={elementId}
      // autoComplete=off does not disable chrome address filling;
      // we have to set it to an unrecognized value
      // https://stackoverflow.com/a/57810447
      autoComplete='nope'
    />
  );
}

Address2Field.propTypes = {
  label: PropTypes.string,
  errors: PropTypes.object.isRequired,
  prepend: PropTypes.string,
  elementId: PropTypes.string,
};

Address2Field.defaultProps = {
  label: 'Apartment number',
  prepend: '',
};

export function CityField({ prepend, label, errors, elementId, required }) {
  return (
    <InputGroup
      name={`${prepend}city`}
      label={label}
      errors={errors}
      elementId={elementId}
      required={required}
      autoComplete='nope'
    />
  );
}

CityField.propTypes = {
  label: PropTypes.string,
  errors: PropTypes.object.isRequired,
  prepend: PropTypes.string,
  elementId: PropTypes.string,
  required: PropTypes.bool,
};

CityField.defaultProps = {
  label: 'City',
  prepend: '',
};

export function StateField({
  prepend,
  label,
  errors,
  elementId,
  required,
  isMailingAddress,
}) {
  return (
    <SelectGroup
      label={label}
      name={`${prepend}state`}
      options={isMailingAddress ? mailingStateEnums : stateEnums}
      errors={errors}
      elementId={elementId}
      required={required}
      autoComplete='nope'
      addBlank
      blankLabel='Select a state'
    />
  );
}

StateField.propTypes = {
  label: PropTypes.string,
  prepend: PropTypes.string,
  errors: PropTypes.object.isRequired,
  elementId: PropTypes.string,
  required: PropTypes.bool,
  isMailingAddress: PropTypes.bool,
};

StateField.defaultProps = {
  label: 'State',
  prepend: '',
};

export function ZipcodeField({ prepend, label, errors, elementId, required }) {
  return (
    <InputGroup
      name={`${prepend}zipcode`}
      inputMode='numeric'
      label={label}
      errors={errors}
      elementId={elementId}
      required={required}
      autoComplete='nope'
    />
  );
}

ZipcodeField.propTypes = {
  label: PropTypes.string,
  errors: PropTypes.object.isRequired,
  prepend: PropTypes.string,
  elementId: PropTypes.string,
  required: PropTypes.bool,
};

ZipcodeField.defaultProps = {
  label: 'ZIP Code',
  prepend: '',
};

function AddressBlock({
  prepend,
  errors,
  elementId,
  isMailingAddress,
  required,
  isAddress2Displayed,
  isStateOnly,
  isCollapsed,
}) {
  // If we already have the full address stored in state, display the fields broken out regardless
  const person = useSelector((state) => state.person);
  const addressAlreadyKnown =
    person.address1 && person.city && person.state && person.zipcode;

  const [hideSecondaryFields, setHideSecondaryFields] = useState(
    isCollapsed && !addressAlreadyKnown
  );
  const [address1Label, setAddress1Label] = useState(
    hideSecondaryFields ? 'Address' : 'Street address'
  );
  const [address1HelpText, setAddress1HelpText] = useState(
    hideSecondaryFields ? 'Start typing address' : ''
  );

  const onAddressSelect = () => {
    setHideSecondaryFields(false);
    setAddress1Label('Street address');
    setAddress1HelpText('');
  };

  if (isStateOnly) {
    return (
      <div className='space-y-5'>
        <StateField
          prepend={prepend}
          errors={errors}
          elementId={elementId.state}
          required={required}
          isMailingAddress={isMailingAddress}
        />
      </div>
    );
  }

  return (
    <div className='space-y-5'>
      <Address1Field
        errors={errors}
        prepend={prepend}
        elementId={elementId.address1}
        required={required}
        onSelect={onAddressSelect}
        label={address1Label}
        helpText={address1HelpText}
        hasManualOption={hideSecondaryFields}
      />
      {!hideSecondaryFields && (
        <>
          {isAddress2Displayed && (
            <Address2Field
              errors={errors}
              prepend={prepend}
              elementId={elementId.address2}
            />
          )}
          <CityField
            errors={errors}
            prepend={prepend}
            elementId={elementId.city}
            required={required}
          />
          <StateField
            prepend={prepend}
            errors={errors}
            elementId={elementId.state}
            required={required}
            isMailingAddress={isMailingAddress}
          />
          <ZipcodeField
            errors={errors}
            prepend={prepend}
            elementId={elementId.zipcode}
            required={required}
          />
        </>
      )}
    </div>
  );
}

AddressBlock.propTypes = {
  errors: PropTypes.object,
  prepend: PropTypes.string,
  elementId: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipcode: PropTypes.string,
  }),
  isMailingAddress: PropTypes.bool,
  required: PropTypes.bool,
  isAddress2Displayed: PropTypes.bool,
  isStateOnly: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};

AddressBlock.defaultProps = {
  elementId: {},
  isAddress2Displayed: true,
  isStateOnly: false,
  isCollapsed: false,
};

export default AddressBlock;
